import { SeoHead } from '@/components/seo-head';
import { DashboardSidebar } from '@/components/dashboard-sidebar';
import {
  MobileBottomNav,
  useMobileBottomNavInset,
  useMobileBottomNavVisible,
} from '@/components/mobile-bottom-nav';
import { MemberDisabledScreen } from '@/components/team/member-disabled-screen';
import { useActingProviderGuard } from '@/hooks/use-acting-provider';
import { useMembership } from '@/hooks/use-membership';
import { useSafeAreaColorEffect } from '@/hooks/use-safe-area-color';
import { onMemberDisabled } from '@/lib/session-events';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuthStore } from '@/store/authStore';
import { useSidebar } from '@/store/sidebarStore';
import { Stack } from 'expo-router';
import React, { useEffect } from 'react';
import { ActivityIndicator, Platform, View, useWindowDimensions } from 'react-native';
import { useThemedHex } from '@/hooks/use-theme-colors';

/** Alinhado ao root layout que força light mode por enquanto */
const DASHBOARD_SAFE_AREA_COLOR = '#f5f5f5'; // neutral-100, como o fundo
/** Menu mobile é branco; safe area deve acompanhar quando aberto */
const SIDEBAR_MOBILE_SAFE_AREA_COLOR = '#ffffff';

export default function DashboardLayout() {
  const th = useThemedHex();
  const { isOpen, closeSidebar, isCollapsed, toggleCollapsed } = useSidebar();
  const { isAuthenticated, fetchProfile } = useAuthStore();
  const { isDisabled, membership } = useMembership();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  // Web-mobile também usa o bottom nav: a navbar do topo (com o hambúrguer que
  // abria o drawer) morreu com o Shell G, e as telas que não usam PageLayout
  // ficariam sem nenhum acesso à navegação.
  const showBottomNav = useMobileBottomNavVisible();
  const bottomNavInset = useMobileBottomNavInset();
  const insets = useSafeAreaInsets();

  const safeAreaColor =
    isMobile && isOpen ? SIDEBAR_MOBILE_SAFE_AREA_COLOR : DASHBOARD_SAFE_AREA_COLOR;
  useSafeAreaColorEffect(th(safeAreaColor));
  useActingProviderGuard();

  // 403 MEMBER_DISABLED no meio do uso: o /user/me novo traz o status e a
  // tela abaixo assume.
  useEffect(() => onMemberDisabled(() => void fetchProfile()), [fetchProfile]);

  // O redirecionamento é feito pelo InitialLayout no _layout.tsx
  // Mostra loading enquanto redireciona se não autenticado
  if (!isAuthenticated) {
    return (
      <View className="flex-1 items-center justify-center">
        {/* É este ramo que sai no HTML estático das rotas logadas. */}
        <SeoHead noindex />
        <ActivityIndicator size="large" className="text-primary" />
      </View>
    );
  }

  if (isDisabled && membership) {
    return <MemberDisabledScreen organizationName={membership.organizationName} />;
  }

  const layoutStyle = Platform.OS === 'web' ? { minHeight: '100dvh' as const } : undefined;
  if (isMobile) {
    return (
      <View className="flex-1 bg-canvas" style={layoutStyle}>
        <SeoHead noindex />
        <DashboardSidebar
          isOpen={isOpen}
          onClose={closeSidebar}
          isCollapsed={isCollapsed}
          onToggleCollapse={toggleCollapsed}
        />

        {/*
          O inset do topo era carregado pela navbar mobile, apagada no Shell G.
          Fica aqui para valer também nas telas que não usam PageLayout.
        */}
        <View
          className="flex-1"
          style={{
            paddingTop: insets.top,
            // Sem o nav (formulários), o próprio rodapé desvia da home
            // indicator (`useFormFooterInset`) para o fundo dele ir até a borda.
            ...(showBottomNav ? { paddingBottom: bottomNavInset } : null),
          }}
        >
          <Stack
            screenOptions={{
              headerShown: false,
            }}
          >
            <Stack.Screen name="index" />
            <Stack.Screen name="(provider)" />
            <Stack.Screen name="(client)" />
          </Stack>
        </View>

        {showBottomNav ? (
          <View className="absolute bottom-0 left-0 right-0">
            <MobileBottomNav />
          </View>
        ) : null}
      </View>
    );
  }

  return (
    <View className="flex-1 bg-canvas" style={layoutStyle}>
      {/* Área logada: não entra em buscador nem em crawler de IA. */}
      <SeoHead noindex />
      <DashboardSidebar
        isOpen={isOpen}
        onClose={closeSidebar}
        isCollapsed={isCollapsed}
        onToggleCollapse={toggleCollapsed}
      >
        <View className="flex-1">
          <Stack
            screenOptions={{
              headerShown: false,
            }}
          >
            <Stack.Screen name="index" />
            <Stack.Screen name="(provider)" />
            <Stack.Screen name="(client)" />
          </Stack>
        </View>
      </DashboardSidebar>
    </View>
  );
}
